/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener('rebuy.ready', function(event) {
  document.querySelectorAll(".rebuy-color-label").forEach(label => {
        label.addEventListener("click", function () {
            const color = this.getAttribute("title");
            console.log("Clicked color swatch:", color);

            const productBlock = this.closest(".rebuy-product-block");
            console.log("Found product block:", productBlock);

            const select = productBlock ? productBlock.querySelector(".rebuy-select") : null;
            console.log("Found select dropdown:", select);

            if (select) {
                let matched = false;
                for (let option of select.options) {
                    console.log("Checking option:", option.innerText);
                    if (option.innerText.includes(color)) {
                        select.value = option.value;
                        console.log("Updated select value to:", select.value);

                        // 🔥 Force change event so Rebuy detects the update
                        select.dispatchEvent(new Event("change", { bubbles: true }));
                        matched = true;
                        break;
                    }
                }
                if (!matched) {
                    console.warn("No matching option found for color:", color);
                }
            } else {
                console.warn("No select dropdown found for color swatch.");
            }
        });
    });
});

// Get all elements with the class "product-block-options__item"
const productOptionsItems = document.querySelectorAll('.product-block-options__item');

// Loop through each element and add a click event listener
productOptionsItems.forEach(item => {
    item.addEventListener('click', function() {
        const variantUrl = this.getAttribute('data-option-variant-url');
        const parentBlockInnerInner = this.closest('.block-inner-inner');
        const productLinkImg = parentBlockInnerInner.querySelector('.image-cont .product-link');
        productLinkImg.href = variantUrl;
        const parentInnerer = this.closest('.innerer');
        const productLinkText = parentInnerer.querySelector('.product-link');
        productLinkText.href = variantUrl;
    });
});

document.addEventListener("DOMContentLoaded", function() {
// Get all elements with the class "image-page-button--previous & image-page-button--next"
const buttonPrevious = document.querySelectorAll('.image-page-button--previous');
const buttonNext = document.querySelectorAll('.image-page-button--next');

// Loop through each element and add a click event listener
buttonPrevious.forEach(item => {
    item.addEventListener('click', function() {
      setTimeout(() => {
        const imageCont = this.closest('.image-cont');
        const productLink = imageCont.getElementsByClassName('product-link');
        const activeImage = imageCont.getElementsByClassName('product-block__image--active');
        const dataMediaId = activeImage[0].getAttribute('data-media-id');
        const variantUrl = productLink[0].getAttribute('href');
        const parentBlockInnerInner = this.closest('.block-inner-inner');
        const productBlockOptions = parentBlockInnerInner.getElementsByClassName('product-block-options__inner');
        const productBlockOptionsItem = productBlockOptions[0].querySelectorAll('.product-block-options__item');
        // Iterate through the product items
        for (var i = 0; i < productBlockOptionsItem.length; i++) {
            // Get the value of the data-media attribute for the current item
            const dataMedia = productBlockOptionsItem[i].getAttribute('data-media');
            
            // Check if the current data-media matches the data-media-id
            if (dataMedia === dataMediaId) {
                const currentHref = productBlockOptionsItem[i].getAttribute('data-option-variant-url');
                productLink[0].href = currentHref;
                break;
            }
        }
      }, 100); // 100ms delay
    });
});

// Loop through each element and add a click event listener
buttonNext.forEach(item => {
    item.addEventListener('click', function() {
      setTimeout(() => {
        const imageCont = this.closest('.image-cont');
        const productLink = imageCont.getElementsByClassName('product-link');
        const activeImage = imageCont.getElementsByClassName('product-block__image--active');
        const dataMediaId = activeImage[0].getAttribute('data-media-id');
        const variantUrl = productLink[0].getAttribute('href');
        const parentBlockInnerInner = this.closest('.block-inner-inner');
        const productBlockOptions = parentBlockInnerInner.getElementsByClassName('product-block-options__inner');
        const productBlockOptionsItem = productBlockOptions[0].querySelectorAll('.product-block-options__item');
        // Iterate through the product items
        for (var i = 0; i < productBlockOptionsItem.length; i++) {
            // Get the value of the data-media attribute for the current item
            const dataMedia = productBlockOptionsItem[i].getAttribute('data-media');
            
            // Check if the current data-media matches the data-media-id
            if (dataMedia === dataMediaId) {
                const currentHref = productBlockOptionsItem[i].getAttribute('data-option-variant-url');
                productLink[0].href = currentHref;
                break;
            }
        }
      }, 100); // 100ms delay
    });
});

});

document.addEventListener("DOMContentLoaded", function() {
    // Grab every .product-link on the page
    let productLinks = document.querySelectorAll('.product-link');

    // Loop through each .product-link
    productLinks.forEach(function(productLink) {
        try {
            // Grab the child element .product-block__image--active data-media-id
            let mediaElement = productLink.querySelector('.product-block__image--active');
            if (!mediaElement) {
                return;
            }
            let mediaId = mediaElement.dataset.mediaId;
            
            // Go to the parent element .block-inner-inner
            let blockInnerInner = productLink.closest('.block-inner-inner');
            if (!blockInnerInner) {
                return;
            }

            // Go down to the children .product-block-options__inner
            let productBlockOptionsInner = blockInnerInner.querySelector('.product-block-options__inner');
            if (!productBlockOptionsInner) {
                return;
            }

            // Loop through every child and find the corresponding data-media
            let matchingElement = Array.from(productBlockOptionsInner.children).find(function(child) {
                return child.dataset.media === mediaId;
            });

            if (!matchingElement) {
                return;
            }

            // Grab the data-option-variant-url of the matching element
            let variantUrl = matchingElement.dataset.optionVariantUrl;

            if (variantUrl) {
                // Update the .product-link href with the variant URL
                productLink.href = variantUrl;
            } else {
            }
        } catch (error) {
            console.error('Error processing product link:', productLink, error);
        }
    });
});

const engravingPopupBox = document.getElementById('engraving-popup');
const engravingPopupBoxSpan = document.querySelector('#engraving-popup span');
const engravingPopupImage = document.getElementById('engraving-popup-image');
let engravingPopupBoxText;


function executeWhenElementPresent(selector, callback) {
    const element = document.querySelector(selector);
    
    if (element) {
        // If element is already present, execute the callback
        callback(element);
    } else {
        // Otherwise, set up a MutationObserver to watch for the element
        const observer = new MutationObserver((mutationsList, observer) => {
            const element = document.querySelector(selector);
            if (element) {
                observer.disconnect(); // Stop observing
                callback(element); // Execute the callback
            }
        });
        
        // Start observing the target node (document.body) for childList changes
        observer.observe(document.body, { childList: true, subtree: true });
    }
}

// Usage example:
executeWhenElementPresent('.tpo_option-set-wrapper', (element) => {
    // Insert engraving image
    // Select the parent element
    var parentElement = document.querySelector('.tpo_option-type-color-swatches-wrapper');

    // Create the new div element with an img inside it
    var newDiv = document.createElement('div');
    newDiv.id = 'tpo_buttons-wrapper-before';
    newDiv.setAttribute('onclick', 'toggleEngravingPopupImage()');

    // Insert the new div inside the parent element
    parentElement.insertBefore(newDiv, parentElement.firstChild);
    // Do something with the element
    engravingPopupBoxText = document.getElementById('engraving-popup-text');
    engravingPopupBoxSpan.innerText = engravingPopupBoxText.innerText;
    // Select the elements
        const tooltipElement = document.querySelector('.tpo_buttons-tooltip');
        const labelElement = document.querySelector('label.tpo_shape_circle.tpo_undefined');

        // Check if the elements exist
        if (tooltipElement && labelElement) {
            // Extract the value inside parentheses
            const tooltipText = tooltipElement.innerText;
            const match = tooltipText.match(/\(([^)]+)\)/);
            const valueInParentheses = match ? match[1] : '';

            // Function to update the innerText based on the presence of the class
            const updateText = () => {
                if (labelElement.classList.contains('tpo_is_checked_buttons')) {
                    labelElement.innerText = `Click to remove engraving`;
                } else {
                    labelElement.innerText = `Add Engraving (${valueInParentheses})`;
                }
            };

            // Add click event listener to the label element
            labelElement.addEventListener('click', updateText);

            // Initial update
            updateText();
        }
});

function toggleEngravingPopup() {
  if (engravingPopupBox.style.display === 'none') {
    engravingPopupBox.style.display = 'block';
  } else {
    engravingPopupBox.style.display = 'none';
  }
}

function toggleEngravingPopupImage() {
  if (engravingPopupImage.style.display === 'none') {
    engravingPopupImage.style.display = 'block';
  } else {
    engravingPopupImage.style.display = 'none';
  }
}


document.addEventListener("DOMContentLoaded", function() {
  const button = document.querySelector('country-selector .custom-select__btn');
  const dropdown = document.querySelector('country-selector ul[aria-activedescendant="annbar-localization-country-opt-0"]');

  // Function to filter options and remove span
  function filterOptions() {
    const options = dropdown.querySelectorAll('.custom-select__option');
    const currenciesSeen = new Set();

    options.forEach(option => {
      const currencyElement = option.querySelector('.country-option__currency');
      const nameElement = option.querySelector('.country-option__name');

      // Remove the name span element
      if (nameElement) {
        nameElement.remove();
      }

      const currency = currencyElement.textContent.trim();

      if (currenciesSeen.has(currency)) {
        option.style.display = 'none';
      } else {
        currenciesSeen.add(currency);
        option.style.display = 'flex'; // Ensure the first option for each currency is visible
      }
    });
  }

  // Create an observer to watch for changes in the dropdown's visibility
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.attributeName === 'aria-hidden') {
        if (dropdown.getAttribute('aria-hidden') === 'false') {
          // Dropdown is now visible
          filterOptions();
        }
      }
    }
  });

  // Start observing the dropdown for attribute changes
  observer.observe(dropdown, { attributes: true });
});


// Select first available variant
document.addEventListener('DOMContentLoaded', function() {
  let observerInitialized = false;
  let observerFieldsets;

  // Function to handle the change event in the first and second fieldsets
  function handleChange() {

    // Get all fieldsets with the class "option-selector"
    const fieldsets = document.querySelectorAll('fieldset.option-selector');

    // Check if there are at least three fieldsets
    if (fieldsets.length < 3) return;

    // Get the third fieldset
    const thirdFieldset = fieldsets[2];

    // Unselect all "input.opt-btn" in the third fieldset
    const inputs = thirdFieldset.querySelectorAll('input.opt-btn');
    for (const input of inputs) {
      if(input.checked && !input.classList.contains('is-nonexistent')){
      return;
      }
      input.checked = false;
    };

    // Check the first "input.opt-btn" that does not have the class "is-unavailable"
    for (const input of inputs) {
      if (!input.classList.contains('is-unavailable')) {
        input.checked = true;
        forceChangeEvent(input);
        break;
      }
    }
  }

  // Force change event
  function forceChangeEvent(input) {
    var forceEvent = new Event('change', { bubbles: true });
    input.dispatchEvent(forceEvent);
  }

  // Function to set up MutationObserver on input elements in the third fieldset
  function observeThirdFieldsetChanges() {
    if (observerInitialized) {
      return;
    }
    observerInitialized = true;

    // Get the third fieldset
    const thirdFieldset = document.querySelector('fieldset.option-selector:nth-of-type(3)');
    const inputs = thirdFieldset.querySelectorAll('input.opt-btn');

    observerFieldsets = new MutationObserver(mutations => {
      observerFieldsets.disconnect(); // Temporarily disconnect the observer

      handleChange();

      // Reconnect the observer after handling changes
      inputs.forEach(input => {
        observerFieldsets.observe(input, { attributes: true });
      });
    });

    inputs.forEach(input => {
      observerFieldsets.observe(input, { attributes: true });
    });
  }

  // Get the first and second fieldsets
  const firstFieldset = document.querySelector('fieldset.option-selector:nth-of-type(1)');
  const secondFieldset = document.querySelector('fieldset.option-selector:nth-of-type(2)');

  // Add change event listeners to all "input.opt-btn" elements in the first and second fieldsets
  [firstFieldset, secondFieldset].forEach(fieldset => {
    const inputs = fieldset.querySelectorAll('input.opt-btn');
    inputs.forEach(input => {
      input.addEventListener('change', function() {
        if (this.checked) {
          observeThirdFieldsetChanges();
        }
      });
    });
  });
});